import React, { useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

export default function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Deletion',
  entityName,
  entityDetail,
  description,
  warning,
  busy = false,
  error = '',
  confirmLabel = 'Delete',
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open && !busy) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, busy, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={!busy ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-title"
    >
      <div
        className="bg-[#12121a] border border-white/10 rounded-2xl p-6 w-full max-w-lg shadow-2xl text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3.5 mb-4">
          <div className="p-2.5 rounded-xl bg-red-500/10 text-red-400 border border-red-500/20 shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="confirm-delete-title" className="text-lg font-semibold text-white">
              {title}
            </h3>
            {description && (
              <p className="text-sm text-white/60 mt-1 leading-relaxed">
                {description}
              </p>
            )}
          </div>
          {!busy && (
            <button
              type="button"
              onClick={onClose}
              className="text-white/40 hover:text-white text-2xl leading-none -mt-1 p-1"
              aria-label="Close dialog"
            >
              &times;
            </button>
          )}
        </div>

        {entityName && (
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3.5 my-4">
            <div className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-0.5">
              Record to delete
            </div>
            <div className="text-white font-medium text-base truncate">
              {entityName}
            </div>
            {entityDetail && (
              <div className="text-xs text-white/50 mt-1 truncate">
                {entityDetail}
              </div>
            )}
          </div>
        )}

        {warning && (
          <div className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mb-4 leading-relaxed">
            {warning}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-300 bg-red-500/15 border border-red-500/30 rounded-xl p-3 mb-4 leading-relaxed flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-400" />
            <span className="flex-1">{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6 pt-3 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-xl border border-white/15 text-white/80 hover:text-white hover:bg-white/5 text-sm font-medium transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-600/20"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{busy ? 'Deleting…' : confirmLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
